/** **셀러가 이번 판에 넣기로 확인·확정한 장**을 정하는 판정 (PRD `7.6.13f` `A-34` ·
 *  `7.7.3a` `W-31` · `13.1` `O-73`).
 *
 * ## ⭐ 왜 shared 에 있는가
 *
 * 이 집합이 곧 청구다. `PublishInput.confirmedImageIds` 에 실려 나가고, 서버는 그 교집합에만
 * 돈을 붙인다(`apps/api/src/lib/publish.ts`). **앱(`A-34`)과 웹(`W-31`)이 각자 세면 같은
 * 작업이 폰에서 7장, PC 에서 8장으로 청구된다.** 화면이 둘이고 판정은 하나여야 한다.
 *
 * ## ⛔ 여기서 `undefined` 를 만들지 않는다
 *
 * `confirmedImageIds` 가 `undefined` 면 서버는 **「이 축을 아직 안 보내는 클라이언트」** 로
 * 읽어 **만든 장을 전부 얼리고 전부 청구한다**. 2026-09-24 까지 화면 둘 다 그 경로였고,
 * 그동안 법무가 요구한 성질(`L-F44`(4))은 **문서에만 서 있었다.** 이 파일이 돌려주는 것은
 * 언제나 **실제 id 목록**이고, 고를 것이 아예 없으면 `canConfirm` 이 `false` 라 화면이
 * 버튼을 잠근다 — 빈 배열을 보내 409 를 받는 길로 가지 않는다.
 *
 * ## ⛔ `끄기` 는 `지우기` 가 아니다
 *
 * 꺼진 장은 `JobImage` 로 그대로 남고 다음 승인·내보내기에서 다시 켤 수 있다. 그래서 이
 * 파일의 낱말이 전부 `켜다`·`끄다`이고 `제외`·`삭제`·`빼기` 가 아니다(PRD `7.5.9`).
 */

import { CUTOUT_UNIT_PRICE_KRW } from "./cost";
import type { DetailJobView, JobImageView } from "./domain";

/** 이번 판에 **들어갈 수 있는 장**. 만들어진 장만이다.
 *
 * **올린 순번으로 정렬해 돌려준다.** 화면이 각자 정렬하면 앱의 3번째 칸과 웹의 3번째 칸이
 * 다른 사진이 되고, 셀러가 끈 것과 꺼진 것이 갈린다. */
export function confirmableImages(job: DetailJobView): JobImageView[] {
  return job.images.filter((img) => img.status === "DONE").sort((a, b) => a.sortOrder - b.sortOrder);
}

/** ⛔ **기본은 전부 켜짐이다** (PRD `7.6.13f` 판정 1).
 *
 * 셀러가 고른 적이 없어서 이 화면이 생긴 것이지 **안 고른 것이 잘못이 아니다.** 기본을
 * 꺼짐으로 두면 화면은 곧 `아직 안 고르셨습니다` 같은 딱지를 달게 되고, 그 딱지가 금지어인
 * 이유가 이것이다(`7.5.9` 판정 10). */
export function defaultConfirmedIds(job: DetailJobView): string[] {
  return confirmableImages(job).map((img) => img.id);
}

/** 확인·확정 화면이 한 번에 읽는 판정 전부. */
export interface ConfirmSelection {
  /** 켜 둔 장. **화면이 격자·목록을 이 순서로 그린다.** */
  on: JobImageView[];
  /** 서버로 보낼 집합. ⛔ **이 값을 `undefined` 로 바꾸지 마라** — 머리말의 그 경로다. */
  confirmedImageIds: string[];
  /** 켜 둔 장수. 금액 줄과 `판에 들어갈 장` 줄이 **같은 이 값**을 쓴다. */
  onCount: number;
  /** 이 판의 금액. **켜진 장수를 따라 움직이는 것이 이 값의 일이다**(PRD `7.6.13f`). */
  amount: number;
  /** 확정할 수 있는가. **켜 둔 장이 하나도 없으면 `false`** 라 화면이 버튼을 잠근다.
   *
   * ⛔ **여기서 막는 것이 빈 배열을 보내는 것보다 낫다.** 빈 배열은 서버에서
   * `NOT_CONFIRMED` 로 돌아오는데, 그 왕복이 셀러에게 주는 것은 **누르고 나서 받는 거절**
   * 하나다. 누를 수 없다는 사실은 누르기 전에 보여야 한다(`7.1` 원칙 4). */
  canConfirm: boolean;
  /** ⭐ **대표컷 자리가 비는가** (`13.1` `O-73` 후속. 정책 미정).
   *
   * 블록 배정은 **올린 순번**이 정한다(`blockTypeForUploadOrder`. 0번이 `hero`). 그래서
   * **셀러가 순번 0 을 끄고 확정하면 그 판에는 `hero` 파일이 없고 전부 `gallery` 가 된다.**
   * 상세페이지 맨 위가 비는 것이라 셀러가 의도한 결과가 아닐 수 있다.
   *
   * ⛔ **이 칸은 그 사실을 알릴 뿐 막지 않는다.** 막을지 순번을 다시 매길지는 제품 결정이고
   * (`design-manager`·`prd-manager` 자리), **결정이 나기 전에 코드가 먼저 고르면 그 선택이
   * 정책이 된다.** 아무 말도 안 하는 쪽만은 피한다 — 화면이 말하지 않으면 셀러는 대표컷이
   * 빠진 판을 받고 나서야 안다.
   *
   * ⚠️ **`assignedBlockType` 을 보지 않는다.** 그 칸은 `JobImageView` 에 없어서 화면이 읽을
   * 수 없다. 서버는 `assignedBlockType ?? blockTypeForUploadOrder(...)` 로 푸므로, 배정이
   * 따로 박힌 작업에서는 이 값이 헛다리를 짚을 수 있다. 지금 배정을 박는 경로는 없다. */
  heroOff: boolean;
}

/** 켜고 끈 결과를 판정으로 바꾼다.
 *
 * **끈 장을 인자로 받는다. 켠 장이 아니다.** 기본이 전부 켜짐이라 화면이 들고 있어야 하는
 * 상태는 **셀러가 손댄 것**뿐이고, 그래야 장이 늘거나 줄어도(다시 만들어 성공한 장) 새로
 * 온 장이 **켜진 채로** 합류한다. 켠 쪽을 들고 있으면 새 장이 조용히 꺼진 채 들어와
 * 청구에서 빠진다.
 */
export function confirmSelection(
  job: DetailJobView,
  offImageIds: ReadonlySet<string>,
): ConfirmSelection {
  const candidates = confirmableImages(job);
  const on = candidates.filter((img) => !offImageIds.has(img.id));
  return {
    on,
    confirmedImageIds: on.map((img) => img.id),
    onCount: on.length,
    // 만들 판의 값이라 **오늘 단가로 셈한다.** `A-25` 의 `다시 만들면 ₩500` 과 같은 성격의
    // 견적이다(아직 일어나지 않은 일의 값).
    // ⛔ **이 줄을 정산·환불 금액에 쓰지 마라.** 그쪽 정본은 저장된 행이고 단가가 한 번만
    //    바뀌어도 이 곱셈은 **셀러가 동의한 적 없는 금액**을 적는다
    //    (`docs/DATA_DICTIONARY.md` 14.3 ① · 15.5 규약 1).
    amount: CUTOUT_UNIT_PRICE_KRW * on.length,
    canConfirm: on.length > 0,
    heroOff: candidates.some((img) => img.sortOrder === 0 && offImageIds.has(img.id)),
  };
}

/** 이미 확정해 둔 판을 **그대로 다시 내보낼 때** 보낼 집합 (PRD `7.6.13f` 판정 5).
 *
 * `W-40` 은 격자가 없다 — 셀러가 고르는 자리는 `W-31`·`A-34` 이고 여기서는 **그때 고른 것을
 * 그대로 다시 보낸다.** 그래야 `publishVersion` 이 같은 판으로 떨어져 새 판이 서지 않고,
 * 이 화면도 확인을 다시 묻지 않는다.
 *
 * **판의 파일에서 올린 순번을 읽어 되짚는다.** `JobVersionFileView` 에 `imageId` 가 없어서
 * 순번으로 잇는데, 그 `sortOrder` 가 곧 `JobImage.sortOrder` 다(`lib/versions.ts` 가 그 값을
 * 그대로 적는다).
 *
 * ⚠️ **되짚어서 아무것도 안 남으면 기본값으로 떨어진다.** 그 사이 고른 장이 전부 파기된
 * 경우인데, 빈 배열을 보내면 `NOT_CONFIRMED`(셀러가 안 골랐다)로 거절돼 **화면이 정반대
 * 사실을 말하게 된다.** 셀러는 골랐다. */
export function confirmedIdsForVersion(
  job: DetailJobView,
  versionFileSortOrders: readonly number[],
): string[] {
  const candidates = confirmableImages(job);
  if (versionFileSortOrders.length === 0) return candidates.map((img) => img.id);
  const wanted = new Set(versionFileSortOrders);
  const matched = candidates.filter((img) => wanted.has(img.sortOrder));
  return (matched.length > 0 ? matched : candidates).map((img) => img.id);
}

/** 판을 만들지 못한 두 사유의 **셀러 문구**. 서버 코드(`PublishRejection`)와 짝이다.
 *
 * ## ⛔ 둘을 뭉개지 않는다. 셀러가 할 일이 정반대다
 *
 * - **`NOT_CONFIRMED`** — 하나도 안 골랐다. **셀러가 다시 골라야 한다.**
 * - **`NO_RESULT`** — 골랐는데 **줄 수 있는 것이 없다.** 다시 골라도 소용없다.
 *
 * 한 문장으로 합치면 앞쪽 셀러는 손을 놓고 뒤쪽 셀러는 같은 버튼을 계속 누른다.
 *
 * ## ⚠️ 이 두 문장은 **정본이 아직 없다**
 *
 * `docs/PRD.md` 에 두 거절의 화면 문구가 없다(2026-09-24 실측. `NOT_CONFIRMED` 는 PRD 에
 * 한 번도 안 나온다). 아래 `NO_RESULT` 는 **`apps/web/src/lib/edit-messages.ts` 에 이미
 * 있던 문장을 옮긴 것**이고, `NOT_CONFIRMED` 는 용어 표(`7.5.9`)가 확정한 낱말
 * (**`판에 들어갈 장`**·**`켜다`**)만으로 지은 잠정 문장이다. **`design-manager` 가 정본을
 * 세우면 그 판으로 갈아 끼운다.** ⛔ 그때 한쪽만 고치지 마라 — 앱과 웹이 이 상수 하나를
 * 읽는 것이 이 자리의 요점이다. */
export const PUBLISH_REJECTION_TEXT = {
  NOT_CONFIRMED: "판에 들어갈 장이 하나도 켜져 있지 않습니다. 한 장 이상 켜 주세요.",
  NO_RESULT: "만든 장이 하나도 없어 확정할 것이 없습니다.",
} as const;
